import { Flame, PackageSearch } from "lucide-react";
import Link from "next/link";
import { Suspense, useEffect, useState } from "react";

import { ProductGrid } from "@/components/product/product-grid";
import { SortSelect } from "@/components/product/sort-select";
import { useSearchParamsObject } from "@/hooks/use-search-params-object";
import { getCategories, queryProducts, type ProductPage } from "@/lib/queries";
import type { SearchParams } from "@/types";

/**
 * `app/products/page.tsx`.
 *
 * Same markup and same two data sources; the queries now run through the API
 * and re-run whenever the query string changes.
 */
export function ProductsPage() {
  const params = useSearchParamsObject();
  const activeCategory = params.category || "All";

  const [categories, setCategories] = useState<string[]>([]);
  const [result, setResult] = useState<ProductPage | null>(null);

  const queryKey = JSON.stringify(params);

  useEffect(() => {
    let active = true;
    void getCategories().then((value) => {
      if (active) setCategories(value);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    void queryProducts(params as SearchParams, 18).then((page) => {
      if (active) setResult(page);
    });
    return () => {
      active = false;
    };
    // `queryKey` is the serialised query string: this re-runs on navigation
    // while keeping a stable dependency (`params` is a new object each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  function pageHref(nextPage: number) {
    const next = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) next.set(key, String(value));
    });
    next.set("page", String(nextPage));
    return `/products?${next.toString()}`;
  }

  function categoryHref(cat: string) {
    const next = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value && key !== "category" && key !== "page") next.set(key, String(value));
    });
    if (cat !== "All") next.set("category", cat);
    return `/products?${next.toString()}`;
  }

  if (!result) {
    return (
      <div className="mx-auto max-w-[1500px] px-3 py-4 sm:px-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="h-64 animate-pulse rounded-lg bg-slate-100 dark:bg-white/5" />
          ))}
        </div>
      </div>
    );
  }

  const { items, total, page, totalPages } = result;

  return (
    <div className="mx-auto max-w-[1500px] px-3 py-4 sm:px-4">
      {/* Category Pills Header Bar */}
      <div className="mb-4 overflow-x-auto pb-1 no-scrollbar">
        <div className="flex items-center gap-2 min-w-max">
          <Link
            href={categoryHref("All")}
            className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition shadow-2xs ${
              activeCategory === "All"
                ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                : "border border-slate-200 bg-white text-slate-700 hover:border-slate-400 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
            }`}
          >
            All Products
          </Link>
          {categories.map((catName) => (
            <Link
              key={catName}
              href={categoryHref(catName)}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition shadow-2xs ${
                activeCategory === catName
                  ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                  : "border border-slate-200 bg-white text-slate-700 hover:border-slate-400 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
              }`}
            >
              {catName}
            </Link>
          ))}
        </div>
      </div>

      {/* Results Header with Sorting and Badges */}
      <div className="mb-3 flex flex-col justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3.5 shadow-2xs dark:border-white/10 dark:bg-slate-900 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-black text-red-600 dark:bg-red-950/40 dark:text-red-400">
            <Flame className="h-3.5 w-3.5" />
            <span>Super Deals</span>
          </div>
          <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
            {total > 0 ? (
              <>
                <strong className="text-slate-950 dark:text-white">{total}</strong> products found
                {activeCategory !== "All" && (
                  <> in <strong className="text-slate-950 dark:text-white">{activeCategory}</strong></>
                )}
              </>
            ) : (
              "No products found"
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Suspense fallback={<div className="h-9 w-36" />}>
            <SortSelect />
          </Suspense>
        </div>
      </div>

      {/* Main 6-Column Product Grid */}
      {items.length > 0 ? (
        <ProductGrid products={items} columns={6} />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center shadow-xs dark:border-white/10 dark:bg-slate-900">
          <PackageSearch className="mx-auto h-12 w-12 text-slate-300" />
          <h2 className="mt-3 text-lg font-bold text-slate-900 dark:text-white">No products found</h2>
          <p className="mt-1 text-xs text-slate-500">Try selecting another category or clear your search filters.</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-3">
          <Link
            href={pageHref(Math.max(1, page - 1))}
            aria-disabled={page <= 1}
            className="rounded border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-800 shadow-2xs hover:bg-slate-50 aria-disabled:pointer-events-none aria-disabled:opacity-40 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
          >
            &larr; Previous
          </Link>
          <span className="text-xs font-bold text-slate-600 dark:text-slate-400">
            Page {page} of {totalPages}
          </span>
          <Link
            href={pageHref(Math.min(totalPages, page + 1))}
            aria-disabled={page >= totalPages}
            className="rounded border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-800 shadow-2xs hover:bg-slate-50 aria-disabled:pointer-events-none aria-disabled:opacity-40 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200"
          >
            Next &rarr;
          </Link>
        </div>
      )}
    </div>
  );
}